// Helpers for interactive drawing answers that also ask for a typed answer
// (spec.written = { prompt, answer, require_drawing }). When a spec has `written`, the answer value is
// stored as { interactive: [...placements], text: "..." } instead of a bare placement array.
// Shared by StudentPaperTake and StudentMockExamTake.

export const interactiveWrittenOf = (spec) => {
  if (!spec?.written || typeof spec.written !== 'object') return null;
  const w = spec.written;
  const prompt = typeof w.prompt === 'string' ? w.prompt.trim() : '';
  // Leftover written.answer alone is authoring residue (student spec strips the answer) — do not
  // show a companion text box or treat the part as half-written.
  if (prompt || w.require_drawing !== undefined) return w;
  return null;
};

// Unwrap the written companion { interactive, text }. The inner value is NOT always an array: an
// edge-drawing part ("plot D, then join ABCD") submits the object shape { points, edges }, and a
// cut-drag part submits { cut, offsets }. Reading only `Array.isArray(v.interactive)` treated those as
// empty, so the canvas was handed [] on the very next render and the point the student had just
// tapped disappeared again — the part could never be answered at all.
const unwrapWritten = (v) =>
  v && !Array.isArray(v) && v.interactive !== undefined
    && !Array.isArray(v.points) && !Array.isArray(v.cut) && !Array.isArray(v.edges)
    && !Array.isArray(v.lines) && v.none === undefined
    ? v.interactive
    : v;

const isLinePair = (l) =>
  Array.isArray(l) && l.length >= 2 && Array.isArray(l[0]) && Array.isArray(l[1]);

// "Draw any lines of symmetry. Some may have none": on a shape with none the right answer is to draw
// nothing, which an empty array cannot express — it is also what an untouched canvas looks like, so the
// part read as unanswered and the mark was never awarded. The student declares it instead, and the
// declaration travels alongside the lines as { lines: [...], none: true }.
export const interactiveLinesOf = (raw) => {
  const v = unwrapWritten(raw);
  if (Array.isArray(v)) return v.filter(isLinePair);
  if (v && !Array.isArray(v) && Array.isArray(v.lines)) return v.lines.filter(isLinePair);
  return [];
};

export const interactiveDeclaredNone = (raw) => {
  const v = unwrapWritten(raw);
  return !!(v && !Array.isArray(v) && v.none === true);
};

// cut-drag answers are an object { cut: [[x,y],[x,y]], offsets: [[dx,dy],[dx,dy]] } — passed through
// whole (the backend grades the object). Other kinds store a bare placement array (or the written
// wrapper around one). cut-drag with a written companion is not supported.
export const interactiveArrayOf = (raw) => {
  const v = unwrapWritten(raw);
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.cut)) return v;
  // reflect/rotate/closed-polygon edge shapes: { points:[[x,y]…], edges:["gx,gy|gx,gy"…] } — the
  // placements are the points; the graded outline travels alongside on the value object.
  if (v && Array.isArray(v.points)) return v.points;
  // edges-only ("join the printed points", no new points placed): the edges are the whole answer, so
  // treat their presence like a non-empty placement array rather than reporting length 0.
  if (v && !Array.isArray(v) && Array.isArray(v.edges) && v.edges.length > 0) return v.edges;
  if (v && !Array.isArray(v) && Array.isArray(v.lines)) return v.lines;
  return [];
};

// The value to hand to the canvas and to SUBMIT — preserves the whole shape so graded extras (the
// reflect/rotate/closed-polygon outline in { points, edges }, a cut-drag object, or a shape_label) survive
// round-trip. Use this instead of interactiveArrayOf wherever the value is fed back into the canvas or
// sent to the server; interactiveArrayOf stays for length/content checks that only care about placements.
export const interactiveValueOf = (raw) => {
  const v = unwrapWritten(raw);
  if (v && !Array.isArray(v) && Array.isArray(v.points)) return v; // { points, edges?, shape_label? }
  if (v && !Array.isArray(v) && Array.isArray(v.cut)) return v;    // cut-drag object
  if (v && !Array.isArray(v) && typeof v.shape_label === "string") return v;
  // edges-only, no plotted points: preserve the whole { edges } object so the graded outline survives —
  // interactiveArrayOf's edges array alone would lose the "these are edges, not points" shape.
  if (v && !Array.isArray(v) && Array.isArray(v.edges) && v.edges.length > 0) return v;
  // { lines, none } must survive whole: dropping the flag turns "this shape has none" back into an
  // untouched canvas the moment the value is round-tripped through the canvas or submitted.
  if (v && !Array.isArray(v) && (Array.isArray(v.lines) || v.none !== undefined)) return v;
  // { vertex, rays } — an `angle` answer. The vertex rides along so the grader can read the submission
  // on its own, so the whole object has to survive; interactiveArrayOf would keep only the arms.
  if (v && !Array.isArray(v) && Array.isArray(v.rays)) return v;
  // A construction reading ("measure AB") can be only { measures } — the line itself is printed.
  if (v && !Array.isArray(v) && v.measures && typeof v.measures === "object" && !Array.isArray(v.measures)) return v;
  return interactiveArrayOf(v);
};

export const interactiveShapeLabelOf = (raw) => {
  const v = unwrapWritten(raw);
  return v && !Array.isArray(v) && typeof v.shape_label === "string" ? v.shape_label.trim() : "";
};

export const interactiveHasContent = (raw) => {
  // Tally-cell answers store a bare mark count (integer). 0 means empty / cleared.
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return true;
  if (typeof raw === "string" && /^\d+$/.test(raw.trim()) && Number(raw) > 0) return true;
  const v = unwrapWritten(raw);
  const a = interactiveArrayOf(v);
  if (Array.isArray(a) && a.length > 0) return true;
  // Edges-only questions ("join the three printed corners"): no_new_points means the student never
  // places a point, so the drawn edges are the whole answer. Without this the answer counts as empty —
  // the palette shows the part unanswered and the submit path drops the row before it is graded.
  if (v && !Array.isArray(v) && Array.isArray(v.edges) && v.edges.length > 0) return true;
  // An angle answer is the arms the student swung out; the vertex alone is the printed figure.
  if (v && !Array.isArray(v) && Array.isArray(v.rays) && v.rays.length > 0) return true;
  // An explicit "no lines of symmetry" IS the answer — the only one this shape accepts.
  if (interactiveDeclaredNone(v)) return true;
  if (interactiveShapeLabelOf(v)) return true;
  if (v && !Array.isArray(v) && Array.isArray(v.segments) && v.segments.length > 0) return true;
  if (v && !Array.isArray(v) && Array.isArray(v.circles) && v.circles.length > 0) return true;
  if (v && !Array.isArray(v) && v.measures && typeof v.measures === "object"
    && Object.values(v.measures).some((m) => String(m ?? "").trim() !== "")) return true;
  return !!(a && Array.isArray(a.cut) && a.cut.length >= 2);
};

export const interactiveWrittenText = (v) =>
  v && !Array.isArray(v) && typeof v.text === "string" ? v.text : "";

// Review canvases for reflect / rotate / closed polygon expect { points, edges }. A take-page
// submission stored as a bare point list would otherwise auto-join in an order the student never drew.
export const interactiveReviewValueOf = (raw, spec) => {
  if (!interactiveHasContent(raw)) return null;
  const v = interactiveValueOf(raw);
  const kind = spec?.kind || "";
  if (Array.isArray(v) && ["reflect", "rotate", "polygon"].includes(kind)) {
    return { points: v, edges: [] };
  }
  return v;
};
